import { useCallback } from 'react';

/**
 * Hook to handle Tab and Shift+Tab navigation between input fields
 * @param fieldRefs Array of refs to input fields in tab order
 * @returns Object with ref setter function and keydown handler
 */
export const useTabNavigation = (fieldRefs: React.RefObject<HTMLInputElement | HTMLSelectElement>[]) => {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      if (event.key !== 'Tab') {
        return;
      }

      const isShiftTab = event.shiftKey;
      const nextIndex = isShiftTab ? currentIndex - 1 : currentIndex + 1;

      // Check if we're at the boundaries
      if (nextIndex < 0 || nextIndex >= fieldRefs.length) {
        // Let default browser behavior handle moving outside this group
        return;
      }

      // Prevent default tab behavior
      event.preventDefault();
      event.stopPropagation();

      // Focus the next/previous field
      const nextField = fieldRefs[nextIndex]?.current;
      if (nextField) {
        nextField.focus();
        // Select all text if it's an input field
        if (nextField instanceof HTMLInputElement) {
          nextField.select();
        }
      }
    },
    [fieldRefs]
  );

  return { handleKeyDown };
};

